import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import SEOHead from '../components/SEOHead';
import useDocumentTitle from '../hooks/useDocumentTitle';

interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  adminResponse?: string;
  respondedAt?: string;
  respondedBy?: {
    id: string;
    username: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

const AdminContactSubmissions: React.FC = () => {
  useDocumentTitle('Cognidaily | Contact Submissions');
  const [submissions, setSubmissions] = useState<ContactSubmission[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<ContactSubmission | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [responseError, setResponseError] = useState<string | null>(null);
  const [responseSuccess, setResponseSuccess] = useState(false);

  const loadSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = { limit: 200 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const res = await axios.get<{
        success: boolean;
        data: { submissions: ContactSubmission[] };
        message?: string;
      }>('/api/admin/contact-submissions', { params });

      if (!res.data.success) {
        throw new Error(res.data.message || 'Failed to load submissions');
      }
      setSubmissions(res.data.data.submissions || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load submissions.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedSubmission) {
        setSelectedSubmission(null);
      }
    };

    if (selectedSubmission) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [selectedSubmission]);

  const handleViewSubmission = async (id: string) => {
    try {
      const res = await axios.get<{
        success: boolean;
        data: ContactSubmission;
      }>(`/api/admin/contact-submissions/${id}`);

      if (res.data.success) {
        setSelectedSubmission(res.data.data);
        setResponseText(res.data.data.adminResponse || '');
        setResponseError(null);
        setResponseSuccess(false);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load submission.');
    }
  };

  const handleRespond = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubmission || !responseText.trim()) {
      setResponseError('Response text is required');
      return;
    }

    if (responseText.trim().length < 10) {
      setResponseError('Response must be at least 10 characters');
      return;
    }

    try {
      setResponding(true);
      setResponseError(null);
      setResponseSuccess(false);

      const res = await axios.post(`/api/admin/contact-submissions/${selectedSubmission.id}/respond`, {
        response: responseText.trim()
      });

      if (res.data.success) {
        setResponseSuccess(true);
        // Reload submissions to get updated data
        await loadSubmissions();
        // Reload the selected submission
        await handleViewSubmission(selectedSubmission.id);
        // Clear response after a moment
        setTimeout(() => {
          setResponseSuccess(false);
        }, 3000);
      } else {
        setResponseError(res.data.message || 'Failed to send response');
      }
    } catch (err: any) {
      setResponseError(err?.response?.data?.message || err?.message || 'Failed to send response.');
    } finally {
      setResponding(false);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const res = await axios.put(`/api/admin/contact-submissions/${id}/status`, {
        status: newStatus
      });

      if (res.data.success) {
        await loadSubmissions();
        if (selectedSubmission && selectedSubmission.id === id) {
          await handleViewSubmission(id);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to update status.');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'admin-contact-status--pending';
      case 'in_progress':
        return 'admin-contact-status--in-progress';
      case 'resolved':
        return 'admin-contact-status--resolved';
      case 'closed':
        return 'admin-contact-status--closed';
      default:
        return '';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="admin-page">
      <SEOHead
        title="Admin | Contact Submissions"
        description="Review and respond to contact form submissions."
      />
      <div>
        <header className="admin-page__header">
          <div>
            <h1 className="admin-page__title">Contact</h1>
            <p className="admin-page__subtitle">Review and respond to contact form submissions.</p>
          </div>
          <div className="admin-page__filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-sm)' }}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            <button
              type="button"
              className="btn btn-secondary btn-xs"
              onClick={loadSubmissions}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </header>

        {error && (
          <div className="admin-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="admin-layout-split">
          <div>
            {loading ? (
              <div className="admin-loading">Loading…</div>
            ) : submissions.length === 0 ? (
              <p className="admin-loading">No submissions.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((submission) => (
                      <tr
                        key={submission.id}
                        className={selectedSubmission?.id === submission.id ? 'admin-contact-row--selected' : ''}
                      >
                        <td>{formatDate(submission.createdAt)}</td>
                        <td>{submission.name}</td>
                        <td>{submission.email}</td>
                        <td>{submission.subject}</td>
                        <td>
                          <span className={`admin-contact-status ${getStatusBadgeClass(submission.status)}`}>
                            {submission.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-xs btn-primary"
                            onClick={() => handleViewSubmission(submission.id)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {selectedSubmission && createPortal(
          <div className="admin-modal-overlay" onClick={() => setSelectedSubmission(null)}>
            <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-modal__header">
                <h2>Submission Details</h2>
                <button
                  className="btn btn-ghost btn-xs"
                  onClick={() => setSelectedSubmission(null)}
                  aria-label="Close modal"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="admin-modal__body">
                <div className="admin-usage-card" style={{ marginBottom: 'var(--space-4)' }}>
                  <h3>From</h3>
                  <p><strong>Name:</strong> {selectedSubmission.name}</p>
                  <p><strong>Email:</strong> <a href={`mailto:${selectedSubmission.email}`}>{selectedSubmission.email}</a></p>
                  <p><strong>Date:</strong> {formatDate(selectedSubmission.createdAt)}</p>
                </div>

                <div className="admin-detail-section">
                  <h3>Subject</h3>
                  <p>{selectedSubmission.subject}</p>
                </div>

                <div className="admin-detail-section">
                  <h3>Message</h3>
                  <div className="admin-contact-message">{selectedSubmission.message}</div>
                </div>

                <div className="admin-detail-section">
                  <h3>Status</h3>
                  <select
                    value={selectedSubmission.status}
                    onChange={(e) => handleStatusChange(selectedSubmission.id, e.target.value)}
                    className="admin-contact-status-select"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                {selectedSubmission.adminResponse && (
                  <div className="admin-detail-section">
                    <h3>Previous Response</h3>
                    <div className="admin-contact-response">
                      {selectedSubmission.adminResponse}
                    </div>
                    {selectedSubmission.respondedBy && (
                      <p className="admin-contact-response-meta">
                        Responded by {selectedSubmission.respondedBy.username} on {formatDate(selectedSubmission.respondedAt || '')}
                      </p>
                    )}
                  </div>
                )}

                <div className="admin-detail-section">
                  <h3>Send Response</h3>
                  <form onSubmit={handleRespond}>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      className="admin-contact-response-input"
                      placeholder="Type your response here..."
                      rows={8}
                      disabled={responding}
                    />
                    <div className="admin-contact-response-char-count">
                      {responseText.length}/5000 characters
                    </div>
                    {responseError && (
                      <div className="admin-contact-response-error">{responseError}</div>
                    )}
                    {responseSuccess && (
                      <div className="admin-contact-response-success">
                        Response sent successfully! Email has been sent to the user.
                      </div>
                    )}
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={responding || !responseText.trim() || responseText.trim().length < 10}
                    >
                      {responding ? 'Sending...' : 'Send Response'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default AdminContactSubmissions;
